import { ROUTES } from '../contracts/routes';

// Where Stripe sends the user back to.
//
// ── WHY THE CLIENT HAS TO SUPPLY THESE ──────────────────────────────────────
//
// createCheckoutSession and createPortalSession both fall back to
// `process.env.APP_ORIGIN || ALLOWED_ORIGINS[0]` when the caller sends nothing.
// APP_ORIGIN is set in the function tests and NOWHERE ELSE — not in CI, not in
// any .env — so that fallback resolved to the hardcoded first entry,
// https://mogged.app, for every caller in every environment.
//
// The client sent nothing. So a member on mogged-mvp-dev.web.app, on
// localhost:3000, or even on www.mogged.app finished checkout and was
// redirected to a DIFFERENT ORIGIN.
//
// Firebase Auth persists to IndexedDB scoped to the origin. A different origin
// is a different session, which is to say: no session. The user paid, got
// bounced to an origin where they were signed out, and AuthGate sent them to
// the sign-in page. Reported as "kicked out once trial started".
//
// The same value is Stripe Checkout's back arrow (`cancel_url`), which is why
// "unable to go back from the checkout screen" was the same bug wearing a
// different hat, and the portal's `return_url` too.
//
// ── WHY PASSING A URL FROM THE CLIENT IS SAFE HERE ──────────────────────────
//
// A caller-supplied redirect is normally an open-redirect vector — Stripe will
// happily send a just-authenticated user wherever it is told. That is exactly
// why the server runs every one of these through `safeRedirectUrl` against an
// origin allowlist and falls back when the origin is not ours.
//
// The allowlist was built for this. Nobody had wired the client up to use it.
//
// `window.location.origin` is the browser's own idea of where it is running,
// not user input — it cannot be spoofed by a link or a query param.

function appOrigin() {
  return typeof window !== 'undefined' && window.location
    ? window.location.origin
    : '';
}

/** Absolute URL on the CURRENT origin for one of our routes. */
export function returnUrlFor(routePath) {
  const origin = appOrigin();
  // Empty in a non-browser context (SSR, a test with no jsdom). The server
  // treats a missing url as "use the fallback", which is the old behaviour —
  // no worse than before, and never a broken relative redirect.
  return origin ? `${origin}${routePath}` : undefined;
}

/**
 * The pair every checkout needs.
 *
 * Undefined values are dropped so the callable receives an absent key rather
 * than an explicit undefined, which is what `safeRedirectUrl` expects for
 * "fall back".
 */
export function checkoutReturnUrls() {
  const success = returnUrlFor(ROUTES.MEMBERSHIP_SUCCESS);
  const cancel = returnUrlFor(ROUTES.MEMBERSHIP_CANCEL);
  return {
    ...(success ? { success_url: success } : {}),
    ...(cancel ? { cancel_url: cancel } : {}),
  };
}

/** Where the Stripe billing portal returns to. */
export function portalReturnUrls() {
  const url = returnUrlFor(ROUTES.MEMBERSHIP);
  return url ? { return_url: url } : {};
}
